import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  DarkModeOutlined,
  LightModeOutlined,
  ArrowForward,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ColorModeContext } from "../../theme";
import ApiConfig from "../../Config";
import authService from "../../repository/authRepository";

const AdminAuth = ({ setIsAuthenticated }) => {
  const { toggleColorMode } = React.useContext(ColorModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setSnackbarMessage("Please enter your email and password.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarOpen(false);

    try {
      const response = await authService.adminIdLogin(
        formData.email,
        formData.password,
      );

      const { newToken, role, name, adminId } = response;

      sessionStorage.setItem("newToken", newToken);
      sessionStorage.setItem("role", role);
      sessionStorage.setItem("name", name);
      sessionStorage.setItem("adminId", adminId);

      if (setIsAuthenticated) {
        setIsAuthenticated(true);
      }

      setSnackbarMessage("Welcome back!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      navigate("/dashboard", { replace: true });
    } catch (error) {
      setSnackbarMessage(
        error.response?.data?.message ||
          "Unable to sign in. Please check your credentials.",
      );

      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        backgroundColor: isDark ? "#11100F" : "#F6F3EF",
        color: isDark ? "#F5F2ED" : "#171614",
      }}
    >
      {/* ================= LEFT BRAND SECTION ================= */}
      {!isMobile && (
        <Box
          sx={{
            width: "52%",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "flex-end",
            p: 7,
            background: isDark
              ? "linear-gradient(145deg, #201D1A 0%, #0F0E0D 100%)"
              : "linear-gradient(145deg, #E8E0D6 0%, #D7CABC 100%)",
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: "absolute",
              width: 500,
              height: 500,
              borderRadius: "50%",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.08)" : "rgba(60,45,30,0.08)"
              }`,
              top: "-180px",
              right: "-150px",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 350,
              height: 350,
              borderRadius: "50%",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "rgba(60,45,30,0.06)"
              }`,
              top: "-100px",
              right: "-75px",
            }}
          />

          {/* Brand content */}
          <Box sx={{ position: "relative", zIndex: 2, maxWidth: 580 }}>
            <Typography
              sx={{
                fontSize: 14,
                letterSpacing: "4px",
                textTransform: "uppercase",
                mb: 3,
                color: isDark ? "#C9B79F" : "#806D59",
                fontWeight: 600,
              }}
            >
              Hindustan Wardrobe
            </Typography>

            <Typography
              sx={{
                fontSize: { md: "48px", lg: "64px" },
                lineHeight: 1.05,
                fontWeight: 300,
                letterSpacing: "-2px",
                mb: 3,
                fontFamily: "Georgia, 'Times New Roman', serif",
              }}
            >
              Style that
              <br />
              <Box component="span" sx={{ fontStyle: "italic" }}>
                tells your story.
              </Box>
            </Typography>

            <Typography
              sx={{
                maxWidth: 430,
                fontSize: 16,
                lineHeight: 1.8,
                color: isDark
                  ? "rgba(255,255,255,0.62)"
                  : "rgba(30,25,20,0.65)",
              }}
            >
              Manage your wardrobe experience with elegance, simplicity and
              complete control.
            </Typography>

            <Box
              sx={{
                mt: 6,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 45,
                  height: 1,
                  backgroundColor: isDark ? "#C9B79F" : "#806D59",
                }}
              />

              <Typography
                sx={{
                  fontSize: 12,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  color: isDark ? "#C9B79F" : "#806D59",
                }}
              >
                Admin Collection
              </Typography>
            </Box>
          </Box>

          {/* Bottom decorative number */}
          <Typography
            sx={{
              position: "absolute",
              right: 40,
              bottom: 25,
              fontSize: 100,
              fontWeight: 700,
              opacity: 0.035,
              lineHeight: 1,
            }}
          >
            HW
          </Typography>
        </Box>
      )}

      {/* ================= RIGHT LOGIN SECTION ================= */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            height: 76,
            px: { xs: 3, md: 5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${
              isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"
            }`,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.5px",
            }}
          >
            {isMobile ? "Hindustan Wardrobe" : "HW"}
          </Typography>

          <IconButton
            onClick={toggleColorMode}
            sx={{
              width: 42,
              height: 42,
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"
              }`,
              color: "inherit",
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          >
            {isDark ? <LightModeOutlined /> : <DarkModeOutlined />}
          </IconButton>
        </Box>

        {/* Login area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 3, sm: 5, md: 8 },
            py: 6,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 440,
            }}
          >
            {/* Mobile brand */}
            {isMobile && (
              <Box sx={{ mb: 7 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: isDark ? "#C9B79F" : "#806D59",
                    mb: 2,
                  }}
                >
                  Admin Portal
                </Typography>

                <Typography
                  sx={{
                    fontSize: 38,
                    lineHeight: 1.1,
                    fontWeight: 300,
                    fontFamily: "Georgia, 'Times New Roman', serif",
                  }}
                >
                  Welcome
                  <br />
                  <Box component="span" sx={{ fontStyle: "italic" }}>
                    back.
                  </Box>
                </Typography>
              </Box>
            )}

            {/* Desktop heading */}
            {!isMobile && (
              <>
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: isDark ? "#C9B79F" : "#806D59",
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Admin Portal
                </Typography>

                <Typography
                  sx={{
                    fontSize: 42,
                    lineHeight: 1.1,
                    fontWeight: 300,
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    mb: 2,
                  }}
                >
                  Welcome back
                  <Box component="span" sx={{ fontStyle: "italic" }}>
                    .
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: isDark
                      ? "rgba(255,255,255,0.55)"
                      : "rgba(0,0,0,0.55)",
                    fontSize: 14,
                    mb: 5,
                  }}
                >
                  Sign in to continue to your admin dashboard.
                </Typography>
              </>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    mb: 1,
                    color: isDark ? "#D9D4CE" : "#45413C",
                  }}
                >
                  EMAIL ADDRESS
                </Typography>

                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@hindustanwardrobe.com"
                  required
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      height: 54,
                      backgroundColor: isDark
                        ? "rgba(255,255,255,0.025)"
                        : "rgba(255,255,255,0.65)",
                      "& fieldset": {
                        borderColor: isDark
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(0,0,0,0.12)",
                      },
                      "&:hover fieldset": {
                        borderColor: isDark ? "#C9B79F" : "#806D59",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: isDark ? "#C9B79F" : "#806D59",
                        borderWidth: "1px",
                      },
                    },
                    "& input": {
                      fontSize: 14,
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    mb: 1,
                    color: isDark ? "#D9D4CE" : "#45413C",
                  }}
                >
                  PASSWORD
                </Typography>

                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      height: 54,
                      backgroundColor: isDark
                        ? "rgba(255,255,255,0.025)"
                        : "rgba(255,255,255,0.65)",
                      "& fieldset": {
                        borderColor: isDark
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(0,0,0,0.12)",
                      },
                      "&:hover fieldset": {
                        borderColor: isDark ? "#C9B79F" : "#806D59",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: isDark ? "#C9B79F" : "#806D59",
                        borderWidth: "1px",
                      },
                    },
                    "& input": {
                      fontSize: 14,
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                endIcon={
                  !loading && (
                    <ArrowForward
                      sx={{
                        transition: "transform 0.2s ease",
                      }}
                    />
                  )
                }
                sx={{
                  height: 56,
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: "0.3px",
                  color: "#FFFFFF",
                  backgroundColor: isDark ? "#806D59" : "#292621",
                  "&:hover": {
                    backgroundColor: isDark ? "#967F68" : "#171512",
                    "& .MuiButton-endIcon svg": {
                      transform: "translateX(4px)",
                    },
                  },
                  "&.Mui-disabled": {
                    backgroundColor: isDark ? "#514940" : "#AAA39B",
                    color: "#fff",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Sign in to dashboard"
                )}
              </Button>
            </Box>

            {/* Footer */}
            <Typography
              sx={{
                mt: 5,
                textAlign: "center",
                fontSize: 11,
                color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.4)",
              }}
            >
              © {new Date().getFullYear()} Hindustan Wardrobe · Admin Portal
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            minWidth: 280,
            borderRadius: "6px",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAuth;
